import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import useFleetStore from "../stores/useFleetStore";
import SlideMenu from "../components/SlideMenu";
import FlameIcon from "../components/FlameIcon";
import useCSVParser from "../fleetHooks";
import CSVModal from "./CSVModal";
import FilterModal from "./FilterModal";
import BottomBar from "./BottomBar";
import { formatLastSeen, getFullDateTime } from "../utils/date-utils";
import { formatFirmwareVersion, parseESCVersion, getESCStatusClasses, getESCFilterKey } from "../utils/esc-utils";
import usePortrait from "../utils/usePortrait";
import { PeekButton, findPeekButton } from "../components/ui/Peek";
import { DOCK_MOTION } from "../components/ChatBot/flerpieDock";
import DronePeek from "./DronePeek";

// Column helper lives at module scope on purpose. Called in the component body it
// gets a fresh identity every render, so the columns memo that depends on it never
// holds — and a fresh columns array remounts every cell under flexRender.
const columnHelper = createColumnHelper();

// Menu items for fleet operations
const fleetMenuItems = [
  { to: "/add/multiple", label: "ADD DRONES" },
  { to: "/batch_print", label: "BATCH LABELS" },
];

// Helper: Format a date into a localized string
function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-CA", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });
}

// Helper: Returns FAA status text and icon
function getFAAStatus(faaReg, regExp) {
  if (!faaReg) {
    return {
      text: "Unregistered",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#FF0000" viewBox="0 0 16 16">
          <path fillRule="evenodd" d="M4.475 5.458c-.284 0-.514-.237-.47-.517C4.28 3.24 5.576 2 7.825 2c2.25 0 3.767 1.36 3.767 3.215 0 1.344-.665 2.288-1.79 2.973-1.1.659-1.414 1.118-1.414 2.01v.03a.5.5 0 0 1-.5.5h-.77a.5.5 0 0 1-.5-.495l-.003-.2c-.043-1.221.477-2.001 1.645-2.712 1.03-.632 1.397-1.135 1.397-2.028 0-.979-.758-1.698-1.926-1.698-1.009 0-1.71.529-1.938 1.402-.066.254-.278.461-.54.461h-.777ZM7.496 14c.622 0 1.095-.474 1.095-1.09 0-.618-.473-1.092-1.095-1.092-.606 0-1.087.474-1.087 1.091S6.89 14 7.496 14"/>
        </svg>
      ),
    };
  }

  const now = new Date();
  const expirationDate = new Date(regExp);
  const monthsUntilExpiration =
    (expirationDate.getFullYear() - now.getFullYear()) * 12 +
    (expirationDate.getMonth() - now.getMonth());

  if (expirationDate < now) {
    return {
      text: "Expired",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#FF0000" viewBox="0 0 16 16">
          <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
        </svg>
      ),
    };
  } else if (monthsUntilExpiration <= 4) {
    return {
      text: "Expires Soon",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#FFCC33" viewBox="0 0 16 16">
          <path d="M8.515 1.019A7 7 0 0 0 8 1V0a8 8 0 0 1 .589.022zm2.004.45a7 7 0 0 0-.985-.299l.219-.976q.576.129 1.126.342zm1.37.71a7 7 0 0 0-.439-.27l.493-.87a8 8 0 0 1 .979.654l-.615.789a7 7 0 0 0-.418-.302zm1.834 1.79a7 7 0 0 0-.653-.796l.724-.69q.406.429.747.91zm.744 1.352a7 7 0 0 0-.214-.468l.893-.45a8 8 0 0 1 .45 1.088l-.95.313a7 7 0 0 0-.179-.483m.53 2.507a7 7 0 0 0-.1-1.025l.985-.17q.1.58.116 1.17zm-.131 1.538q.05-.254.081-.51l.993.123a8 8 0 0 1-.23 1.155l-.964-.267q.069-.247.12-.501m-.952 2.379q.276-.436.486-.908l.914.405q-.24.54-.555 1.038zm-.964 1.205q.183-.183.35-.378l.758.653a8 8 0 0 1-.401.432z"/>
          <path d="M8 1a7 7 0 1 0 4.95 11.95l.707.707A8.001 8.001 0 1 1 8 0z"/>
          <path d="M7.5 3a.5.5 0 0 1 .5.5v5.21l3.248 1.856a.5.5 0 0 1-.496.868l-3.5-2A.5.5 0 0 1 7 9V3.5a.5.5 0 0 1 .5-.5"/>
        </svg>
      ),
    };
  } else {
    return {
      text: "Active",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#FFFFFF" viewBox="0 0 16 16">
          <path d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425z"/>
        </svg>
      ),
    };
  }
}

// Helper: Returns a CSS class based on drone condition
function getStatusClass(condition) {
  switch (condition) {
    case "RTF":
      return "bg-green-900/75";
    case "NYC":
      // Not yet complete. It had no tone at all, which read as "no status"
      // rather than as a status, on 400 drones. Blue because the other three are
      // spoken for and this is the only one that is nobody's problem yet.
      return "bg-blue-900/60";
    case "Flag":
      return "bg-yellow-900/75";
    case "OOC":
      return "bg-danger/30";
    default:
      return "";
  }
}

// Custom filter for "Last Seen" column
function lastSeenFilter(row, columnId, value) {
  if (!value) return true;
  const lastSeenTime = row.original.last_seen_time;

  // "Never Seen" filter - only show drones that have never been seen
  if (value === "never") return !lastSeenTime;

  // For other filters, exclude drones that have never been seen
  if (!lastSeenTime) return false;

  const lastSeenDate = new Date(lastSeenTime);
  const now = new Date();
  const diffMonths =
    (now.getFullYear() - lastSeenDate.getFullYear()) * 12 +
    (now.getMonth() - lastSeenDate.getMonth());
  if (value === "1") return diffMonths < 1;
  else if (value === "3") return diffMonths < 3;
  else if (value === "6") return diffMonths > 6;
  else return true;
}

const DroneFleet = () => {
  const drones = useFleetStore((state) => state.drones);
  const flags = useFleetStore((state) => state.flags);
  const refreshFleet = useFleetStore((state) => state.refreshFleet);
  const history = useHistory();
  const location = useLocation();
  const isPortrait = usePortrait();

  // Refresh fleet data when navigating to this page
  useEffect(() => {
    refreshFleet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - refreshFleet is a stable Zustand action

  // CSV parser hook
  const { csvFile, droneIds, error, handleFileChange, parseCSV } = useCSVParser();

  // Read firmware and ESC filter from URL params
  const urlParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const firmwareFromUrl = urlParams.get("firmware") || "";
  const escFromUrl = urlParams.get("esc") || "";

  // State
  const [useCSV, setUseCSV] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 30 });

  // ── HOW MANY ROWS FIT, MEASURED, NOT GUESSED ────────────────────────────────
  //
  // A page of 30 was right for one window size and wrong for every other: a
  // laptop scrolled the table, a tall monitor left half the screen empty. The
  // page size is now whatever the space actually holds.
  //
  // MEASURED off a rendered row rather than a constant, because row height is
  // not ours to predict: portrait uses text-xs and desktop text-sm, cell padding
  // differs with it, and a long customer name can wrap. One real row is the
  // truth for the density currently on screen.
  //
  // No feedback loop, and that rests on the shell having a FIXED height: the
  // container's clientHeight does not depend on its content, so changing the
  // page size cannot change the space and re-trigger this. Under the old `min-h`
  // it would have oscillated.
  const scrollRef = useRef(null);
  const shellRef = useRef(null);

  // The smallest page worth showing. Below this the list stops being a list, so
  // a very short window scrolls the few rows instead of rendering one.
  const MIN_VISIBLE_ROWS = 4;

  // Sorting state with localStorage persistence.
  // Phase C migration: the legacy "fleet_owner" column id was renamed to "customer".
  // If we don't translate it on read, TanStack silently accepts the unknown id and
  // renders an unsorted table with the indicator gone — a regression that only
  // affects existing users whose saved sort happens to be that column.
  const [sorting, setSorting] = useState(() => {
    const fallback = [{ id: "lastSeen", desc: true }];
    try {
      const saved = localStorage.getItem("fleetSorting");
      if (!saved) return fallback;
      const parsed = JSON.parse(saved);
      return parsed.map(s => s.id === "fleet_owner" ? { ...s, id: "customer" } : s);
    } catch {
      return fallback;
    }
  });

  // Column visibility with localStorage persistence
  const defaultColumnVisibility = {
    serialnumber: true,
    flags: false,
    condition: false,
    customer: true,
    ownership: !isPortrait,
    pyro: !isPortrait,
    project_assignment: !isPortrait,
    lastSeen: true,
    faaStatus: !isPortrait,
    firmware: false,
    esc: false,
    lastSeenServer: false,
  };

  const [columnVisibility, setColumnVisibility] = useState(() => {
    try {
      const saved = localStorage.getItem("fleetColumnVisibility");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with defaults for any new columns
        return { ...defaultColumnVisibility, ...parsed };
      }
      return defaultColumnVisibility;
    } catch {
      return defaultColumnVisibility;
    }
  });

  // Update column visibility when orientation changes
  useEffect(() => {
    setColumnVisibility(prev => ({
      ...prev,
      project_assignment: !isPortrait,
      faaStatus: !isPortrait,
    }));
  }, [isPortrait]);

  // Persist sorting to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("fleetSorting", JSON.stringify(sorting));
    } catch (err) {
      console.error("Failed to save sorting:", err);
    }
  }, [sorting]);

  // Persist column visibility to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("fleetColumnVisibility", JSON.stringify(columnVisibility));
    } catch (err) {
      console.error("Failed to save column visibility:", err);
    }
  }, [columnVisibility]);

  // Cart state
  const [cart, setCart] = useState(() => {
    try {
      const raw = localStorage.getItem("droneCart");
      return new Set(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("droneCart", JSON.stringify([...cart]));
    } catch (err) {
      console.error("Failed to save cart:", err);
    }
  }, [cart]);

  const toggleCart = useCallback((serial) => {
    if (!serial) return;
    setCart((prev) => {
      const next = new Set(prev);
      next.has(serial) ? next.delete(serial) : next.add(serial);
      return next;
    });
  }, []);

  const clearCart = useCallback(() => setCart(new Set()), []);

  // The peek: one drone beside the table. Focus goes back to the eye key of whichever
  // drone it was showing when it closes.
  const [peekSerial, setPeekSerial] = useState(null);
  const peekReturn = useRef(null);
  const openPeek = useCallback((serial, from) => {
    peekReturn.current = from;
    setPeekSerial(serial);
  }, []);

  // Filter state
  const [conditionFilterValue, setConditionFilterValue] = useState("");
  const [faaFilterValue, setFAAFilterValue] = useState("");
  const [customerFilterValue, setCustomerFilterValue] = useState("");
  const [projectFilterValue, setProjectFilterValue] = useState("");
  const [lastSeenFilterValue, setLastSeenFilterValue] = useState("");
  const [firmwareFilterValue, setFirmwareFilterValue] = useState(firmwareFromUrl);
  const [escFilterValue, setEscFilterValue] = useState(escFromUrl);

  // Sync firmware filter with URL param
  useEffect(() => {
    setFirmwareFilterValue(firmwareFromUrl);
  }, [firmwareFromUrl]);

  // Sync ESC filter with URL param
  useEffect(() => {
    setEscFilterValue(escFromUrl);
  }, [escFromUrl]);

  // Unique customers & projects
  const uniqueCustomers = useMemo(
    // Drones with no customer FK fall under "Firefly Internal" — surface that as a
    // selectable filter value so ops can isolate the in-house pool.
    () => [
      ...new Set(
        drones.map((d) => d.customer_org_name || "Firefly Internal").filter(Boolean),
      ),
    ],
    [drones]
  );
  const uniqueProjects = useMemo(
    () => [...new Set(drones.map((d) => d.project_assignment).filter(Boolean))],
    [drones]
  );
  // Unique firmwares with labels
  const uniqueFirmwares = useMemo(() => {
    const fwMap = new Map();
    drones.forEach((d) => {
      if (d.firmware_version && !fwMap.has(d.firmware_version)) {
        fwMap.set(d.firmware_version, {
          hash: d.firmware_version,
          label: d.firmware_label || null,
        });
      }
    });
    // Sort: labeled first, then by hash
    return [...fwMap.values()].sort((a, b) => {
      if (a.label && !b.label) return -1;
      if (!a.label && b.label) return 1;
      return (a.label || a.hash).localeCompare(b.label || b.hash);
    });
  }, [drones]);

  // Unique ESC versions (type + version combo)
  const uniqueEscVersions = useMemo(() => {
    const escMap = new Map();
    drones.forEach((d) => {
      const escKey = getESCFilterKey(
        d.esc_version_result,
        d.esc_version_type,
        d.esc_version_major,
        d.esc_version_minor
      );
      if (escKey && !escMap.has(escKey)) {
        escMap.set(escKey, { key: escKey, count: 0 });
      }
      if (escKey) {
        escMap.get(escKey).count++;
      }
    });
    // Sort by count descending
    return [...escMap.values()].sort((a, b) => b.count - a.count);
  }, [drones]);

  // UI state
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [columnsDropdownOpen, setColumnsDropdownOpen] = useState(false);
  const [modalAction, setModalAction] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const cartRef = useRef(null);
  const columnsRef = useRef(null);

  // Close cart drawer and columns dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      if (cartRef.current && !cartRef.current.contains(e.target)) {
        setCartDrawerOpen(false);
      }
      if (columnsRef.current && !columnsRef.current.contains(e.target)) {
        setColumnsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Auto-parse CSV
  useEffect(() => {
    if (csvFile) parseCSV();
  }, [csvFile, parseCSV]);

  // Filter drones by CSV, firmware, and ESC
  const filteredDrones = useMemo(() => {
    let result = drones;

    // CSV filter
    if (useCSV && droneIds.length > 0) {
      result = result.filter(
        (drone) => drone.serialnumber && droneIds.includes(drone.serialnumber)
      );
    }

    // Firmware filter (from URL) - uses firmware_version field (git hash from telemetry)
    if (firmwareFilterValue) {
      result = result.filter((drone) => drone.firmware_version === firmwareFilterValue);
    }

    // ESC filter (from URL or filter modal) - uses ESC type + version combo
    if (escFilterValue) {
      result = result.filter((drone) => {
        const escKey = getESCFilterKey(
          drone.esc_version_result,
          drone.esc_version_type,
          drone.esc_version_major,
          drone.esc_version_minor
        );
        return escKey === escFilterValue;
      });
    }

    return result;
  }, [useCSV, droneIds, drones, firmwareFilterValue, escFilterValue]);

  // Stable clear callbacks for filter pills
  const clearConditionFilter = useCallback(() => setConditionFilterValue(""), []);
  const clearFAAFilter = useCallback(() => setFAAFilterValue(""), []);
  const clearCustomerFilter = useCallback(() => setCustomerFilterValue(""), []);
  const clearProjectFilter = useCallback(() => setProjectFilterValue(""), []);
  const clearLastSeenFilter = useCallback(() => setLastSeenFilterValue(""), []);
  const clearCSVFilter = useCallback(() => setUseCSV(false), [setUseCSV]);
  const clearFirmwareFilter = useCallback(() => {
    setFirmwareFilterValue("");
    // Also clear URL param if it was the only one
    const params = new URLSearchParams(location.search);
    params.delete("firmware");
    const newSearch = params.toString();
    history.replace(newSearch ? `/fleet?${newSearch}` : "/fleet");
  }, [history, location.search]);

  const clearEscFilter = useCallback(() => {
    setEscFilterValue("");
    // Also clear URL param
    const params = new URLSearchParams(location.search);
    params.delete("esc");
    const newSearch = params.toString();
    history.replace(newSearch ? `/fleet?${newSearch}` : "/fleet");
  }, [history, location.search]);

  // Active filters for pills
  const activeFilters = useMemo(() => {
    const filters = [];
    if (conditionFilterValue) filters.push({ key: "condition", label: conditionFilterValue, clear: clearConditionFilter });
    if (faaFilterValue) filters.push({ key: "faa", label: `FAA: ${faaFilterValue}`, clear: clearFAAFilter });
    if (customerFilterValue) filters.push({ key: "customer", label: customerFilterValue, clear: clearCustomerFilter });
    if (projectFilterValue) filters.push({ key: "project", label: projectFilterValue, clear: clearProjectFilter });
    if (lastSeenFilterValue) {
      const labels = { "1": "Past Month", "3": "Past 3 Mo", "6": ">6 Months", "never": "Never Seen" };
      filters.push({ key: "lastSeen", label: labels[lastSeenFilterValue], clear: clearLastSeenFilter });
    }
    if (useCSV && droneIds.length > 0) {
      filters.push({ key: "csv", label: `CSV (${droneIds.length})`, clear: clearCSVFilter });
    }
    if (firmwareFilterValue) {
      const fwMatch = uniqueFirmwares.find((fw) => fw.hash === firmwareFilterValue);
      const fwDisplayLabel = fwMatch?.label || `${firmwareFilterValue.substring(0, 8)}...`;
      filters.push({ key: "firmware", label: `FW: ${fwDisplayLabel}`, clear: clearFirmwareFilter });
    }
    if (escFilterValue) {
      filters.push({ key: "esc", label: `ESC: ${escFilterValue}`, clear: clearEscFilter });
    }
    return filters;
  }, [conditionFilterValue, faaFilterValue, customerFilterValue, projectFilterValue, lastSeenFilterValue, useCSV, droneIds, firmwareFilterValue, uniqueFirmwares, escFilterValue,
      clearConditionFilter, clearFAAFilter, clearCustomerFilter, clearProjectFilter, clearLastSeenFilter, clearCSVFilter, clearFirmwareFilter, clearEscFilter]);

  const clearAllFilters = useCallback(() => {
    setConditionFilterValue("");
    setFAAFilterValue("");
    setCustomerFilterValue("");
    setProjectFilterValue("");
    setLastSeenFilterValue("");
    setGlobalFilter("");
    setUseCSV(false);
    setFirmwareFilterValue("");
    setEscFilterValue("");
    // Clear URL params if present
    if (firmwareFilterValue || escFilterValue) {
      history.replace("/fleet");
    }
  }, [setUseCSV, firmwareFilterValue, escFilterValue, history]);

  // Table columns.
  // Cart state and flags reach the cells through table meta, NOT through this memo's
  // deps: a fresh `cart` Set per toggle would rebuild the whole column set and remount
  // every cell. Only what changes which columns EXIST belongs in the deps.
  const columns = useMemo(
    () => [
      // Cart column
      {
        id: "cart",
        header: ({ table }) => {
          const { cart, clearCart } = table.options.meta;
          return cart.size > 0 ? (
            <button
              type="button"
              title="Clear cart"
              onClick={(e) => { e.stopPropagation(); clearCart(); }}
              className="px-2 py-0 m-0 rounded text-xs font-bold bg-blue-700 hover:bg-blue-600 cart-clear-glow"
            >
              −
            </button>
          ) : null;
        },
        cell: ({ row, table }) => {
          const { cart, toggleCart, openPeek } = table.options.meta;
          const serial = row.original.serialnumber;
          const inCart = cart.has(serial);
          return (
            // The negative margin keeps the 28px peek key inside the pinned row height
            // (docs/features/fleet-list-layout.md).
            <div className="-my-1.5 flex items-center justify-center gap-0.5">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleCart(serial); }}
                className={`px-2 py-0 m-0 rounded text-xs font-semibold
                  ${inCart ? "bg-blue-700 hover:bg-blue-600" : "bg-gray-700 hover:bg-gray-600"}`}
              >
                {inCart ? "−" : "+"}
              </button>
              {serial && <PeekButton name={serial} peekFor={serial} onPeek={(from) => openPeek(serial, from)} />}
            </div>
          );
        },
        enableSorting: false,
        meta: { className: "text-center px-1", style: { width: "1%" } },
      },
      // Serial Number
      columnHelper.accessor("serialnumber", {
        header: isPortrait ? "Serial" : "Serial Number",
        cell: (info) => {
          const serial = info.getValue() || "";
          const displaySerial = isPortrait && serial.length > 7 ? serial.slice(-7) : serial;
          return (
            <div
              title={serial}
              className="font-mono"
              style={{
                maxWidth: isPortrait ? "80px" : "150px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {displaySerial}
            </div>
          );
        },
        enableSorting: true,
        meta: { className: "whitespace-nowrap px-1", style: { width: "1%" } },
      }),
      // Condition
      columnHelper.accessor("condition", {
        header: "Status",
        enableSorting: true,
        filterFn: "equals",
        meta: { className: "whitespace-nowrap text-center px-1", style: { width: "1%" } },
      }),
      // FAA Status
      {
        accessorFn: (row) => getFAAStatus(row.faa_reg, row.reg_exp).text,
        id: "faaStatus",
        header: "FAA",
        cell: ({ row }) => {
          const { icon } = getFAAStatus(row.original.faa_reg, row.original.reg_exp);
          return <div className="flex justify-center">{icon}</div>;
        },
        enableSorting: true,
        meta: { className: "text-center px-1", style: { width: "1%" } },
      },
      // Flags
      {
        id: "flags",
        header: "Flags",
        cell: ({ row, table }) => {
          const { flags } = table.options.meta;
          const droneFlags = flags.filter(
            (flag) => flag.drone_serial === row.original.serialnumber && flag.active
          );
          return droneFlags.map((flag) => flag.descript).join(", ") || "";
        },
        enableSorting: false,
        meta: { className: "truncate" },
      },
      // Customer
      // Customer — joined customer_org_name with explicit fallback for internal pool.
      // accessorFn coerces NULL into "Firefly Internal" so sorting, filtering, and
      // unique-extraction all see a single value for unassigned drones.
      columnHelper.accessor((row) => row.customer_org_name || "Firefly Internal", {
        id: "customer",
        header: isPortrait ? "Cstmr" : "Customer",
        cell: (info) => {
          const value = info.getValue();
          const isInternal = !info.row.original.customer_org_id;
          const display = isInternal
            ? <span className="italic text-amber-300/80">{isPortrait ? "Internal" : value}</span>
            : value;
          return isPortrait ? (
            <span title={value} className="truncate block max-w-[60px]">{display}</span>
          ) : display;
        },
        enableSorting: true,
      }),
      // Ownership — Lease (Firefly retains) / Sale (transferred) / em-dash when
      // there's no customer link at all. Returns `undefined` for unassigned rows
      // so `sortUndefined: 'last'` parks the em-dash bucket at the bottom in both
      // sort directions (TanStack's default null handling places them at one end
      // OR the other depending on direction, which is confusing).
      columnHelper.accessor((row) => {
        if (!row.customer_org_id) return undefined;
        return row.is_internal_pool ? "Lease" : "Sale";
      }, {
        id: "ownership",
        header: isPortrait ? "Own" : "Ownership",
        cell: (info) => {
          const v = info.getValue();
          if (v === undefined) return <span className="text-gray-700">—</span>;
          const color = v === "Lease" ? "text-blue-300" : "text-green-300";
          return <span className={`text-[11px] font-medium ${color}`}>{v}</span>;
        },
        enableSorting: true,
        sortDescFirst: true,
        sortUndefined: "last",
      }),
      // Pyro — flame icon only when pyro_enabled = true
      columnHelper.accessor("pyro_enabled", {
        id: "pyro",
        header: "Pyro",
        cell: (info) =>
          info.getValue() ? (
            <span className="inline-flex items-center justify-center" title="Pyro-enabled">
              <FlameIcon className="w-4 h-4" />
            </span>
          ) : (
            <span className="text-gray-700">—</span>
          ),
        enableSorting: true,
        sortDescFirst: true,
      }),
      // Project
      columnHelper.accessor("project_assignment", {
        header: isPortrait ? "Proj" : "Project",
        cell: (info) => {
          const value = info.getValue() || "";
          return isPortrait ? (
            <span title={value} className="truncate block max-w-[60px]">{value}</span>
          ) : value;
        },
        enableSorting: false,
      }),
      // Last Seen
      {
        id: "lastSeen",
        header: isPortrait ? "Seen" : "Last Seen",
        accessorFn: (row) => row.last_seen_time ? new Date(row.last_seen_time).getTime() : 0,
        cell: ({ row }) => {
          const lastSeenTime = row.original.last_seen_time;
          return (
            <span title={getFullDateTime(lastSeenTime)} className="whitespace-nowrap">
              {formatLastSeen(lastSeenTime)}
            </span>
          );
        },
        filterFn: lastSeenFilter,
        enableSorting: true,
        sortDescFirst: true,
      },
      // Last Seen Server (desktop only)
      ...(!isPortrait ? [{
        id: "lastSeenServer",
        header: "Server",
        accessorFn: (row) => {
          const gcs = row.last_seen_gcs_code;
          if (gcs) return `${gcs} - ${row.last_seen_customer_name || "Firefly"}`;
          return row.last_seen_server || "";
        },
        cell: ({ row }) => {
          const customer = row.original.last_seen_customer_name;
          const gcs = row.original.last_seen_gcs_code;
          const serverIp = row.original.last_seen_server;
          if (!gcs && !serverIp) return <span className="text-gray-500">—</span>;
          const display = gcs ? `${gcs} - ${customer || "Firefly"}` : serverIp;
          return (
            <span title={serverIp || ""} className="whitespace-nowrap">
              {display}
            </span>
          );
        },
        enableSorting: true,
      }] : []),
      // Firmware (desktop only)
      ...(!isPortrait ? [{
        id: "firmware",
        header: "Firmware",
        accessorFn: (row) => row.firmware_label || row.firmware_version || "",
        cell: ({ row }) => {
          const fwVersion = row.original.firmware_version;
          const fwLabel = row.original.firmware_label;
          if (!fwVersion) return <span className="text-gray-500">—</span>;
          const fw = formatFirmwareVersion(fwVersion, fwLabel);
          return (
            <span
              title={fw.title}
              className="font-mono text-xs whitespace-nowrap"
            >
              {fw.display}
            </span>
          );
        },
        enableSorting: true,
      }] : []),
      // ESC Version (desktop only)
      ...(!isPortrait ? [{
        id: "esc",
        header: "ESC",
        accessorFn: (row) => getESCFilterKey(
          row.esc_version_result,
          row.esc_version_type,
          row.esc_version_major,
          row.esc_version_minor
        ) || "",
        cell: ({ row }) => {
          const esc = parseESCVersion(
            row.original.esc_version_result,
            row.original.esc_version_type,
            row.original.esc_version_major,
            row.original.esc_version_minor
          );
          if (esc.display === '-') return <span className="text-gray-500">—</span>;
          return (
            <span className={`text-xs whitespace-nowrap ${getESCStatusClasses(esc.display, esc.status)}`}>
              {esc.display}
            </span>
          );
        },
        enableSorting: true,
      }] : []),
    ],
    [isPortrait]
  );

  // Table instance
  const table = useReactTable({
    data: filteredDrones,
    columns,
    // A fresh meta object per render is correct and cheap: TanStack reads options
    // during render, so cells always see current cart/flags. Memoizing it (or the
    // cells that read it) is what makes it stale.
    meta: { cart, toggleCart, clearCart, flags, openPeek },
    state: { sorting, globalFilter, columnFilters, pagination, columnVisibility },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const preRows = table.getPrePaginationRowModel().rows;

  // The shell ends where the window ends, whatever is above it.
  //
  // This was `calc(100dvh - 48px)` with 48 standing for the navbar, and the
  // navbar is not 48: the document came out a few pixels taller than the
  // viewport, which is enough to hand the page a scrollbar and scroll the sticky
  // header away with it. Measuring the shell's own top removes the guess and
  // survives any later change to the chrome above it.
  //
  // An inline height rather than a class because the value is measured, and a
  // measurement cannot be a Tailwind class. Setting it resizes the scroll
  // container, so the row-fitting effect below re-runs off its own observer.
  useEffect(() => {
    const el = shellRef.current;
    if (!el) return undefined;
    const size = () => {
      const top = el.getBoundingClientRect().top;
      el.style.height = `${Math.max(240, window.innerHeight - top)}px`;
    };
    size();
    window.addEventListener("resize", size);
    window.addEventListener("orientationchange", size);
    return () => {
      window.removeEventListener("resize", size);
      window.removeEventListener("orientationchange", size);
    };
  }, []);

  // Fit the page to the window, and keep fitting it as the window changes.
  //
  // Observes the scroll container, whose height is fixed by the shell, so this
  // fires on a viewport or orientation change and not on its own output. The
  // measurement reads one rendered row and the header, then floors: a partial
  // row at the bottom is the thing being removed, so rounding up would put it
  // straight back.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const fitRows = () => {
      const row = el.querySelector('tbody tr');
      const head = el.querySelector('thead');
      if (!row) return;
      const rowHeight = row.getBoundingClientRect().height;
      // A row measuring nothing means the table is mid-layout or hidden; a
      // division by it would produce a page size of Infinity.
      if (rowHeight < 4) return;
      const headHeight = head ? head.getBoundingClientRect().height : 0;
      const fits = Math.floor((el.clientHeight - headHeight) / rowHeight);
      const next = Math.max(MIN_VISIBLE_ROWS, fits);
      setPagination((prev) => (prev.pageSize === next ? prev : { ...prev, pageSize: next }));
    };

    fitRows();
    const observer = new ResizeObserver(fitRows);
    observer.observe(el);
    return () => observer.disconnect();
    // isPortrait changes the type scale and the padding with it, so the row it
    // measured is no longer the row on screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortrait, preRows.length === 0]);

  // Then check the answer and give a row back if it was one too many.
  //
  // The arithmetic above cannot be exact and should not pretend to be. Row
  // heights are fractional, border-collapse does not report the way you expect,
  // and one wrapped customer name makes a row taller than the row that was
  // measured. Any of those puts the page a few pixels over, which is a scrollbar
  // and a lost sticky header for the sake of one row nobody asked for.
  //
  // So: still overflowing, still above the floor, drop one. Each pass removes a
  // row and therefore height, so it converges in a pass or two and cannot loop.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || el.scrollHeight <= el.clientHeight) return;
    setPagination((prev) => (
      prev.pageSize > MIN_VISIBLE_ROWS ? { ...prev, pageSize: prev.pageSize - 1 } : prev
    ));
  }, [pagination.pageSize, preRows.length, isPortrait, columnVisibility]);
  const cartIds = useMemo(() => Array.from(cart), [cart]);

  const peekDrone = useMemo(
    () => (peekSerial ? drones.find((d) => d.serialnumber === peekSerial) || null : null),
    [drones, peekSerial]
  );
  const peekIndex = peekSerial ? preRows.findIndex((r) => r.original.serialnumber === peekSerial) : -1;

  // Steps in the table's own order, across pages, and turns the page with it so the row
  // being shown is the one on screen when the peek closes.
  const stepPeek = useCallback((dir) => {
    if (!peekSerial || preRows.length === 0) return;
    const at = preRows.findIndex((r) => r.original.serialnumber === peekSerial);
    const nextIndex = ((at < 0 ? 0 : at + dir) + preRows.length) % preRows.length;
    const page = Math.floor(nextIndex / pagination.pageSize);
    if (page !== pagination.pageIndex) setPagination((prev) => ({ ...prev, pageIndex: page }));
    setPeekSerial(preRows[nextIndex].original.serialnumber);
  }, [peekSerial, preRows, pagination.pageSize, pagination.pageIndex]);

  const closePeek = useCallback(() => {
    const key = peekSerial ? findPeekButton(peekSerial) : null;
    if (key) peekReturn.current = key;
    setPeekSerial(null);
  }, [peekSerial]);

  // Visible drone IDs for bulk operations
  const visibleDroneIds = useMemo(() => {
    if (cartIds.length) return cartIds;
    if (activeFilters.length > 0 || globalFilter) {
      return preRows.map((row) => row.original.serialnumber);
    }
    return [];
  }, [cartIds, preRows, activeFilters, globalFilter]);

  // Apply column filters
  useEffect(() => {
    table.setColumnFilters((old) =>
      [...old.filter((f) => f.id !== "condition"), conditionFilterValue ? { id: "condition", value: conditionFilterValue } : null].filter(Boolean)
    );
  }, [conditionFilterValue, table]);

  useEffect(() => {
    table.setColumnFilters((old) =>
      [...old.filter((f) => f.id !== "faaStatus"), faaFilterValue ? { id: "faaStatus", value: faaFilterValue } : null].filter(Boolean)
    );
  }, [faaFilterValue, table]);

  useEffect(() => {
    table.setColumnFilters((old) =>
      [...old.filter((f) => f.id !== "customer"), customerFilterValue ? { id: "customer", value: customerFilterValue } : null].filter(Boolean)
    );
  }, [customerFilterValue, table]);

  useEffect(() => {
    table.setColumnFilters((old) =>
      [...old.filter((f) => f.id !== "project_assignment"), projectFilterValue ? { id: "project_assignment", value: projectFilterValue } : null].filter(Boolean)
    );
  }, [projectFilterValue, table]);

  useEffect(() => {
    table.setColumnFilters((old) =>
      [...old.filter((f) => f.id !== "lastSeen"), lastSeenFilterValue ? { id: "lastSeen", value: lastSeenFilterValue } : null].filter(Boolean)
    );
  }, [lastSeenFilterValue, table]);

  // Navigation
  const handleRowClick = (drone) => {
    history.push(`/drone/${drone.serialnumber}`);
  };

  // Modal actions
  const openCSVModal = (action) => {
    setModalAction(action);
    setIsModalOpen(true);
  };

  const closeCSVModal = () => {
    setModalAction(null);
    setIsModalOpen(false);
  };

  // Check if any filters are active (don't allow adding full unfiltered list)
  const hasActiveFilters = activeFilters.length > 0 || globalFilter;

  // Add filtered to cart (only if filters are active)
  const addFilteredToCart = () => {
    if (!hasActiveFilters) return; // Don't allow adding full unfiltered list
    const filteredSerials = preRows.map((row) => row.original.serialnumber).filter(Boolean);
    if (filteredSerials.length === 0) return;
    setCart((prev) => {
      const next = new Set(prev);
      filteredSerials.forEach((serial) => next.add(serial));
      return next;
    });
  };

  // Export cart to CSV
  const exportCartToCSV = () => {
    if (cart.size === 0) return;
    const cartDrones = drones.filter((drone) => cart.has(drone.serialnumber));
    const headers = ["Serial Number", "Condition", "FAA Registration", "FAA Expiration", "Customer", "Project", "Last Seen"];
    const rows = cartDrones.map((drone) => {
      const lastSeen = drone.last_seen_time ? new Date(drone.last_seen_time).toLocaleDateString() : "";
      const regExp = drone.reg_exp ? formatDate(drone.reg_exp) : "";
      const customer = drone.customer_org_name || "Firefly Internal";
      return [drone.serialnumber || "", drone.condition || "", drone.faa_reg || "", regExp, customer, drone.project_assignment || "", lastSeen]
        .map((field) => {
          const str = String(field);
          if (str.includes(",") || str.includes('"') || str.includes("\n")) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(",");
    });
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `drone_export_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    // A FIXED height, both orientations, not a minimum.
    //
    // The table region below already scrolls itself: flex-1 overflow-hidden
    // wrapping h-full overflow-y-auto, with a sticky thead. None of that can
    // work under `min-h`, because the flex child is then free to grow past the
    // viewport, the inner div's h-full grows with it, and the PAGE ends up doing
    // the scrolling with the header scrolled off the top. Portrait was already
    // fixed; desktop was the one still growing.
    //
    // dvh rather than vh so the unit accounts for browser chrome, matching what
    // portrait already used. 48px is the navbar.
    <div ref={shellRef} className="flex flex-col text-white h-[calc(100dvh-48px)]">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-900/40 border border-red-600 rounded mx-4 mt-2 p-2 text-center">
          <span className="text-red-200 text-sm">{error}</span>
        </div>
      )}

      {/* Header: Search + Cart */}
      <div className="flex-shrink-0 px-4 py-3">
        <div className="flex items-center gap-2">
          {/* Search - Full Width */}
          <div className="flex-1 relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
              xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"
            >
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/>
            </svg>
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search drones..."
              className="w-full bg-gray-800 text-white text-sm pl-9 pr-3 py-2 rounded-lg border border-gray-700
                focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/30"
            />
          </div>

          {/* Cart Button (desktop) */}
          {!isPortrait && (
            <div ref={cartRef} className="relative">
              <button
                onClick={() => setCartDrawerOpen(!cartDrawerOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm
                  ${cart.size > 0
                    ? "bg-primary/20 border-primary text-primary"
                    : "bg-gray-800 border-gray-700 text-gray-400 hover:text-white"}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-7 1a1 1 0 1 1 0 2 1 1 0 0 1 0-2zm7 0a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>
                </svg>
                Cart {cart.size > 0 && `(${cart.size})`}
              </button>

              {/* Cart Dropdown */}
              {cartDrawerOpen && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800">
                    <span className="text-sm font-medium">{cart.size} selected</span>
                    <div className="flex gap-2">
                      <button
                        onClick={addFilteredToCart}
                        disabled={!hasActiveFilters}
                        className={`text-xs ${hasActiveFilters ? "text-teal-400 hover:text-teal-300" : "text-gray-600 cursor-not-allowed"}`}
                        title={hasActiveFilters ? `Add ${preRows.length} filtered drones` : "Apply filters first"}
                      >
                        + Add Filtered ({preRows.length})
                      </button>
                      <button onClick={exportCartToCSV} className="text-xs text-emerald-400 hover:text-emerald-300">
                        Export
                      </button>
                      <button onClick={clearCart} className="text-xs text-red-400 hover:text-red-300">
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {[...cart].length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">Cart is empty</p>
                    ) : (
                      <ul className="divide-y divide-gray-800">
                        {[...cart].map((id) => (
                          <li key={id} className="flex items-center justify-between px-3 py-2 hover:bg-gray-800/50">
                            <span className="font-mono text-xs truncate">{id}</span>
                            <button onClick={() => toggleCart(id)} className="text-gray-500 hover:text-red-400">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                                <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Page actions menu trigger — sits in the bar instead of floating */}
          <button
            onClick={() => setSideMenuOpen(!sideMenuOpen)}
            className="flex items-center justify-center px-3 py-2 rounded-lg border bg-gray-800 border-gray-700
              text-gray-400 hover:text-white transition-all"
            aria-label="Open actions menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"/>
            </svg>
          </button>

        </div>

      </div>

      {/* Results Count + Filter + Badges */}
      <div className="flex-shrink-0 px-4 py-2 text-sm text-gray-400">
        <div className="flex items-center justify-between">
          {/* Columns + Filter Button + Badges */}
          <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
            {/* Columns Toggle (desktop only) */}
            {!isPortrait && (
              <div ref={columnsRef} className="relative">
                <button
                  onClick={() => setColumnsDropdownOpen(!columnsDropdownOpen)}
                  className="flex items-center gap-1.5 text-gray-400 hover:text-white transition-all"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1v7.5a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 1 12.5V5a1 1 0 0 1-1-1V2zm2 3v7.5A1.5 1.5 0 0 0 3.5 14h9a1.5 1.5 0 0 0 1.5-1.5V5H2zm13-3H1v2h14V2zM5 7.5a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1h-5a.5.5 0 0 1-.5-.5z"/>
                  </svg>
                  <span>Columns</span>
                </button>

                {/* Columns Dropdown */}
                {columnsDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl z-50">
                    <div className="px-3 py-2 border-b border-gray-800 text-xs font-medium text-gray-400">
                      Show/Hide Columns
                    </div>
                    <div className="p-2 space-y-1 text-left">
                      {[
                        { id: "serialnumber", label: "Serial Number" },
                        { id: "condition", label: "Status" },
                        { id: "faaStatus", label: "FAA" },
                        { id: "flags", label: "Flags" },
                        { id: "customer", label: "Customer" },
                        { id: "ownership", label: "Ownership" },
                        { id: "pyro", label: "Pyro" },
                        { id: "project_assignment", label: "Project" },
                        { id: "lastSeen", label: "Last Seen" },
                        { id: "lastSeenServer", label: "Server" },
                        { id: "firmware", label: "Firmware" },
                        { id: "esc", label: "ESC" },
                      ].map((col) => (
                        <label
                          key={col.id}
                          className="block px-2 py-1.5 rounded hover:bg-gray-800 cursor-pointer text-sm"
                        >
                          <input
                            type="checkbox"
                            checked={columnVisibility[col.id] !== false}
                            onChange={(e) =>
                              setColumnVisibility((prev) => ({
                                ...prev,
                                [col.id]: e.target.checked,
                              }))
                            }
                            className="mr-2 align-middle"
                          />
                          {col.label}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <button
              onClick={() => setFilterModalOpen(true)}
              className={`flex items-center gap-1.5 transition-all relative flex-shrink-0
                ${activeFilters.length > 0 ? "text-primary" : "text-gray-400 hover:text-white"}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.128.334L10 8.692V13.5a.5.5 0 0 1-.342.474l-3 1A.5.5 0 0 1 6 14.5V8.692L1.628 3.834A.5.5 0 0 1 1.5 3.5v-2z"/>
              </svg>
              <span>Filters</span>
            </button>

            {/* Active Filter Badges - inline with button */}
            {activeFilters.map((filter) => (
              <span
                key={filter.key}
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-800 rounded-full text-xs"
              >
                {filter.label}
                <button onClick={filter.clear} className="text-gray-400 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16">
                    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                  </svg>
                </button>
              </span>
            ))}
            {activeFilters.length > 0 && (
              <button onClick={clearAllFilters} className="text-xs text-red-400 hover:text-red-300 flex-shrink-0">
                Clear
              </button>
            )}
          </div>

          {/* Count */}
          <div className="flex-shrink-0 ml-2">
            <span className="text-white font-medium">{preRows.length}</span> drones
          </div>
        </div>
      </div>

      {/* Table - Scrollable Area */}
      <div className={`flex-1 overflow-hidden ${isPortrait ? "pb-16" : ""}`}>
        <div ref={scrollRef} className="h-full overflow-y-auto">
          <table className={`w-full border-collapse ${isPortrait ? "text-xs" : "text-sm"}`}>
            <thead className="bg-gray-800 text-gray-300 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const { className, style } = header.column.columnDef.meta || {};
                    return (
                      <th
                        key={header.id}
                        className={`${isPortrait ? "px-1 py-2" : "px-3 py-3"} text-left text-xs uppercase tracking-wide cursor-pointer
                          hover:text-primary transition-colors ${className || ""}`}
                        style={style}
                        onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <div className={`flex items-center ${isPortrait ? "gap-1" : "gap-2"}`}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="text-xs text-gray-500">
                              {header.column.getIsSorted() === "asc" ? "▲" : header.column.getIsSorted() === "desc" ? "▼" : "⇅"}
                            </span>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`${isPortrait ? "h-[30px]" : "h-[38px]"} ${getStatusClass(row.original.condition)}
                    ${cart.has(row.original.serialnumber) ? "bg-primary/20 border-l-2 border-l-primary" : ""}
                    cursor-pointer hover:bg-gray-800/50 transition-colors`}
                  onClick={() => handleRowClick(row.original)}
                >
                  {row.getVisibleCells().map((cell) => {
                    const { className, style } = cell.column.columnDef.meta || {};
                    return (
                      <td
                        key={cell.id}
                        className={`${isPortrait ? "px-1 py-1.5" : "px-3 py-2"} border-b border-gray-800 ${className || ""}`}
                        style={style}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Bar */}
      <BottomBar
        table={table}
        totalResults={preRows.length}
        cartSize={cart.size}
        onCartClick={() => setCartDrawerOpen(!cartDrawerOpen)}
        hasSelectedDrones={visibleDroneIds.length > 0}
        onOwnerClick={() => openCSVModal("ownerAssignment")}
        onProjectClick={() => openCSVModal("projectAssignment")}
        onFlagAddClick={() => openCSVModal("bulkFlagAdd")}
        onFlagRemoveClick={() => openCSVModal("bulkFlagRemove")}
        isPortrait={isPortrait}
      />

      {/* Side Menu */}
      <SlideMenu isOpen={sideMenuOpen} onClose={() => setSideMenuOpen(false)} items={fleetMenuItems} />

      {/* Filter Modal */}
      <FilterModal
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        conditionFilterValue={conditionFilterValue}
        setConditionFilterValue={setConditionFilterValue}
        faaFilterValue={faaFilterValue}
        setFAAFilterValue={setFAAFilterValue}
        customerFilterValue={customerFilterValue}
        setCustomerFilterValue={setCustomerFilterValue}
        projectFilterValue={projectFilterValue}
        setProjectFilterValue={setProjectFilterValue}
        lastSeenFilterValue={lastSeenFilterValue}
        setLastSeenFilterValue={setLastSeenFilterValue}
        firmwareFilterValue={firmwareFilterValue}
        setFirmwareFilterValue={setFirmwareFilterValue}
        escFilterValue={escFilterValue}
        setEscFilterValue={setEscFilterValue}
        uniqueCustomers={uniqueCustomers}
        uniqueProjects={uniqueProjects}
        uniqueFirmwares={uniqueFirmwares}
        uniqueEscVersions={uniqueEscVersions}
        csvFile={csvFile}
        handleFileChange={handleFileChange}
        useCSV={useCSV}
        setUseCSV={setUseCSV}
        droneIds={droneIds}
        isPortrait={isPortrait}
      />

      <DronePeek
        drone={peekDrone}
        flags={flags}
        onClose={closePeek}
        returnTo={peekReturn}
        step={peekIndex >= 0 ? { index: peekIndex, total: preRows.length, onStep: stepPeek, noun: "drone" } : undefined}
      />

      {/* CSV Modal */}
      {isModalOpen && (
        <CSVModal droneIds={visibleDroneIds} action={modalAction} closeModal={closeCSVModal} />
      )}

      {/* Mobile Cart Drawer - slides up from behind the bottom bar */}
      {isPortrait && (
        <>
          {/* Backdrop */}
          <div
            className={`scrim transition-opacity duration-300 z-30
              ${cartDrawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            onClick={() => setCartDrawerOpen(false)}
          />
          {/* Drawer. The outer box follows Flerpie's dock and the inner one slides, so each
              keeps its own timing; the outer lets the pointer through when the drawer is down. */}
          <div
            className={`pointer-events-none fixed left-[var(--flerpie-dock,0px)] right-0 bottom-0 z-30 transition-[left] ${DOCK_MOTION}`}
            style={{ marginBottom: "52px" }}
          >
            <div
              className={`pointer-events-auto bg-gray-900 border-t border-gray-700 rounded-t-2xl
                max-h-[60vh] overflow-hidden transition-transform duration-300 ease-out
                ${cartDrawerOpen ? "translate-y-0" : "translate-y-full"}`}
            >
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-600 rounded-full" />
              </div>
              <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-800">
                <span className="font-medium">{cart.size} selected</span>
                <div className="flex gap-3">
                  <button
                    onClick={addFilteredToCart}
                    disabled={!hasActiveFilters}
                    className={`text-sm ${hasActiveFilters ? "text-teal-400" : "text-gray-600"}`}
                  >
                    + Filtered
                  </button>
                  <button onClick={exportCartToCSV} className="text-sm text-emerald-400">Export</button>
                  <button onClick={clearCart} className="text-sm text-red-400">Clear</button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[40vh]">
                {[...cart].length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Cart is empty</p>
                ) : (
                  <ul className="divide-y divide-gray-800">
                    {[...cart].map((id) => (
                      <li key={id} className="flex items-center justify-between px-4 py-3">
                        <span className="font-mono text-sm truncate">{id}</span>
                        <button onClick={() => toggleCart(id)} className="p-2 text-gray-500 hover:text-red-400">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DroneFleet;
